import { ref } from 'vue'
import { defineStore } from 'pinia'

import type {
  CustomerWorkbookNotificationOut,
  WorkbookAssignmentOut,
  WorkbookDetailOut,
  WorkbookNotificationContextOut,
  WorkbookPathAliasOut,
  WorkbookReviewerOut,
  WorkbookReviewOut,
  WorkbookSummaryOut,
  WorkbookCustomerStatisticsOut,
  WorkbookCustomerProgressOut,
} from '@/api'
import {
  archiveNativeWorkbook,
  fetchNativeNotificationContext,
  fetchNativeCustomerStatistics,
  updateNativeCustomerProgress,
  fetchNativeSourceCardBlob,
  fetchNativeInputSourceCardBlob,
  fetchNativeWorkbook,
  fetchNativeWorkbookBlob,
  fetchNativeWorkbookReview,
  listNativeWorkbookReviewers,
  listNativeWorkbookCustomerReviewers,
  listNativeWorkbooks,
  resolveNativeWorkbookPath,
  renameNativeWorkbook,
  renderNativeWorkbookCorrections,
  resetNativeWorkbookCorrections,
  saveNativeWorkbookCorrections,
  sendNativeCustomerNotifications,
  setNativeWorkbookReviewStatus,
  updateNativeWorkbookAssignment,
  updateNativeWorkbookCustomerClaim,
  verifyNativeWorkbookRows,
  type NativeNotificationRequest,
  type NativeWorkbookIdentity,
  type NativeWorkbookQuery,
} from '@/api/nativeWorkbooks'
import type {
  CellEdit,
  WorkbookRowDeletion,
  WorkbookRowInsertion,
} from '@/components/customer/workbookGrid'
import { classifyApiError } from '@/utils/apiError'

export interface NativeWorkbookSession {
  kind: 'native'
  projectId: number
  workbookId: string
  detail: WorkbookDetailOut
  review: WorkbookReviewOut | null
}

export interface WorkbookAssignmentRecoveryState {
  message: string
  controlsDisabled: boolean
}

export interface WorkbookAssignmentMutationResult {
  status: 'saved' | 'saved_refresh_failed' | 'conflict_refreshed' | 'ownership_unknown'
  message?: string
}

export interface WorkbookLifecycleRecoveryState {
  message: string
  controlsDisabled: boolean
}

export interface WorkbookLifecycleMutationResult {
  status: 'saved' | 'conflict_refreshed' | 'removed' | 'state_unknown'
  message?: string
}

export interface NativeWorkbookNotificationRequest extends NativeNotificationRequest {
  expectedEffectiveHash: string
}

const REFRESHABLE_READ_CODES = new Set([
  'workbook_snapshot_invalid',
  'workbook_content_conflict',
  'workbook_review_content_conflict',
  'source_card_lineage_conflict',
])

function projectKey(projectId: number): string {
  return String(projectId)
}

function workbookKey(projectId: number, workbookId: string): string {
  return `${projectId}:${workbookId}`
}

function identityFor(detail: WorkbookDetailOut): NativeWorkbookIdentity {
  return {
    generationId: detail.current_generation_id,
    correctionRevisionId: detail.current_correction_revision_id,
    effectiveHash: detail.effective_content_hash,
    customerVersion: detail.customer_progress?.version ?? 0,
  }
}

export const useNativeWorkbooksStore = defineStore('nativeWorkbooks', () => {
  const listByProject = ref<Map<string, WorkbookSummaryOut[]>>(new Map())
  const totalByProject = ref<Map<string, number>>(new Map())
  const queryByProject = ref<Map<string, NativeWorkbookQuery>>(new Map())
  const requestedQueryByProject = new Map<string, NativeWorkbookQuery>()
  const preparedQueryByProject = new Map<string, string>()
  const loadingByProject = ref<Map<string, boolean>>(new Map())
  const errorByProject = ref<Map<string, string | null>>(new Map())
  const details = ref<Map<string, WorkbookDetailOut>>(new Map())
  const reviews = ref<Map<string, WorkbookReviewOut>>(new Map())
  const reviewersByProject = ref<Map<string, WorkbookReviewerOut[]>>(new Map())
  const customerReviewersByProject = ref<Map<string, WorkbookReviewerOut[]>>(new Map())
  const customerReviewerGenerations = new Map<string, number>()
  const assignmentPending = ref<Set<string>>(new Set())
  const assignmentRecovery = ref<Map<string, WorkbookAssignmentRecoveryState>>(new Map())
  const customerClaimPending = ref<Set<string>>(new Set())
  const customerClaimRecovery = ref<Map<string, WorkbookAssignmentRecoveryState>>(new Map())
  const lifecyclePending = ref<Set<string>>(new Set())
  const lifecycleRecovery = ref<Map<string, WorkbookLifecycleRecoveryState>>(new Map())
  const listGenerations = new Map<string, number>()
  const detailGenerations = new Map<string, number>()
  const acceptedCustomerClaims = new Map<
    string,
    { revision: number; claim: WorkbookSummaryOut['customer_claim'] }
  >()
  let customerClaimRevision = 0
  let epoch = 0
  const portalStatistics = ref<Map<number, WorkbookCustomerStatisticsOut>>(new Map())
  const statisticsGenerations = new Map<number, number>()
  const progressPending = ref<Set<string>>(new Set())
  const scopeWorkbooks = ref(new Map<string, WorkbookSummaryOut | null>())
  const scopeWorkbookGenerations = new Map<string, number>()

  function workbookForScope(projectId: number, scopeId: string): WorkbookSummaryOut | null {
    return scopeWorkbooks.value.get(`${projectId}:${scopeId}`) ?? null
  }

  async function fetchScopeWorkbook(projectId: number, scopeId: string): Promise<void> {
    const startedEpoch = epoch
    const key = `${projectId}:${scopeId}`
    const generation = (scopeWorkbookGenerations.get(key) ?? 0) + 1
    scopeWorkbookGenerations.set(key, generation)
    scopeWorkbooks.value.delete(key)
    const result = await listNativeWorkbooks(projectId, { scopeId, pageSize: 1 })
    requireCurrentEpoch(startedEpoch)
    if (scopeWorkbookGenerations.get(key) !== generation) return
    scopeWorkbooks.value.set(key, result.items[0] ?? null)
  }

  function portalStatisticsFor(projectId: number): WorkbookCustomerStatisticsOut | null {
    return portalStatistics.value.get(projectId) ?? null
  }

  async function fetchPortalStatistics(projectId: number): Promise<WorkbookCustomerStatisticsOut> {
    const startedEpoch = epoch
    const generation = (statisticsGenerations.get(projectId) ?? 0) + 1
    statisticsGenerations.set(projectId, generation)
    const result = await fetchNativeCustomerStatistics(projectId)
    requireCurrentEpoch(startedEpoch)
    if (statisticsGenerations.get(projectId) !== generation)
      throw new Error('The portal totals request was superseded.')
    portalStatistics.value.set(projectId, result)
    return result
  }

  function customerProgressPendingFor(projectId: number, workbookId: string): boolean {
    return progressPending.value.has(workbookKey(projectId, workbookId))
  }

  function cacheCustomerProgress(
    projectId: number,
    workbookId: string,
    progress: WorkbookCustomerProgressOut,
  ): void {
    const detail = detailFor(projectId, workbookId)
    if (detail && (detail.customer_progress?.version ?? 0) <= (progress.version ?? 0))
      cacheDetail({ ...detail, customer_progress: progress })
    listByProject.value.set(
      projectKey(projectId),
      listFor(projectId).map((item) =>
        item.workbook_id === workbookId &&
        (item.customer_progress?.version ?? 0) <= (progress.version ?? 0)
          ? { ...item, customer_progress: progress }
          : item,
      ),
    )
  }

  async function updateCustomerProgress(
    projectId: number,
    workbookId: string,
    identity: NativeWorkbookIdentity,
    status: 'needs_review' | 'in_progress' | 'done',
  ): Promise<WorkbookCustomerProgressOut> {
    const startedEpoch = epoch
    const key = workbookKey(projectId, workbookId)
    if (progressPending.value.has(key))
      throw new Error('A customer status change is already pending.')
    progressPending.value.add(key)
    try {
      const progress = await updateNativeCustomerProgress(projectId, workbookId, identity, status)
      requireCurrentEpoch(startedEpoch)
      cacheCustomerProgress(projectId, workbookId, progress)
      if (queryFor(projectId).customerStatus && queryFor(projectId).customerStatus !== 'all')
        await fetchList(projectId, queryFor(projectId))
      return progress
    } catch (caught) {
      if (epoch === startedEpoch && classifyApiError(caught).status === 409)
        await fetchDetail(projectId, workbookId, { includeReview: false })
      throw caught
    } finally {
      if (epoch === startedEpoch) progressPending.value.delete(key)
    }
  }

  function requireCurrentEpoch(startedEpoch: number): void {
    if (epoch !== startedEpoch) {
      throw new Error('The workbook request was superseded by a session change.')
    }
  }

  function listFor(projectId: number): WorkbookSummaryOut[] {
    return listByProject.value.get(projectKey(projectId)) ?? []
  }

  function totalFor(projectId: number): number {
    return totalByProject.value.get(projectKey(projectId)) ?? 0
  }

  function queryFor(projectId: number): NativeWorkbookQuery {
    return { ...(queryByProject.value.get(projectKey(projectId)) ?? {}) }
  }

  function loadingFor(projectId: number): boolean {
    return loadingByProject.value.get(projectKey(projectId)) ?? false
  }

  function markPreparedList(projectId: number, query: NativeWorkbookQuery): void {
    preparedQueryByProject.set(projectKey(projectId), JSON.stringify(query))
  }

  function consumePreparedList(projectId: number, query: NativeWorkbookQuery): boolean {
    const key = projectKey(projectId)
    const prepared = preparedQueryByProject.get(key)
    preparedQueryByProject.delete(key)
    return prepared === JSON.stringify(query)
  }

  function errorFor(projectId: number): string | null {
    return errorByProject.value.get(projectKey(projectId)) ?? null
  }

  function detailFor(projectId: number, workbookId: string): WorkbookDetailOut | null {
    return details.value.get(workbookKey(projectId, workbookId)) ?? null
  }

  function reviewFor(projectId: number, workbookId: string): WorkbookReviewOut | null {
    return reviews.value.get(workbookKey(projectId, workbookId)) ?? null
  }

  function reviewersFor(projectId: number): WorkbookReviewerOut[] {
    return reviewersByProject.value.get(projectKey(projectId)) ?? []
  }

  function customerReviewersFor(projectId: number): WorkbookReviewerOut[] {
    return customerReviewersByProject.value.get(projectKey(projectId)) ?? []
  }

  function assignmentPendingFor(projectId: number, workbookId: string): boolean {
    return assignmentPending.value.has(workbookKey(projectId, workbookId))
  }

  function assignmentRecoveryFor(
    projectId: number,
    workbookId: string,
  ): WorkbookAssignmentRecoveryState | null {
    return assignmentRecovery.value.get(workbookKey(projectId, workbookId)) ?? null
  }

  function lifecyclePendingFor(projectId: number, workbookId: string): boolean {
    return lifecyclePending.value.has(workbookKey(projectId, workbookId))
  }

  function lifecycleRecoveryFor(
    projectId: number,
    workbookId: string,
  ): WorkbookLifecycleRecoveryState | null {
    return lifecycleRecovery.value.get(workbookKey(projectId, workbookId)) ?? null
  }

  function isCurrentListRequest(startedEpoch: number, key: string, generation: number): boolean {
    return epoch === startedEpoch && listGenerations.get(key) === generation
  }

  function reset(): void {
    scopeWorkbooks.value.clear()
    scopeWorkbookGenerations.clear()
    portalStatistics.value.clear()
    statisticsGenerations.clear()
    progressPending.value.clear()
    epoch += 1
    listByProject.value.clear()
    totalByProject.value.clear()
    queryByProject.value.clear()
    requestedQueryByProject.clear()
    preparedQueryByProject.clear()
    loadingByProject.value.clear()
    errorByProject.value.clear()
    details.value.clear()
    reviews.value.clear()
    reviewersByProject.value.clear()
    customerReviewersByProject.value.clear()
    customerReviewerGenerations.clear()
    assignmentPending.value.clear()
    assignmentRecovery.value.clear()
    customerClaimPending.value.clear()
    customerClaimRecovery.value.clear()
    lifecyclePending.value.clear()
    lifecycleRecovery.value.clear()
    listGenerations.clear()
    detailGenerations.clear()
    acceptedCustomerClaims.clear()
    customerClaimRevision = 0
  }

  function cacheSummary(summary: WorkbookSummaryOut): void {
    const project = projectKey(summary.project_id)
    const list = listByProject.value.get(project)
    if (list) {
      listByProject.value.set(
        project,
        list.map((item) => (item.workbook_id === summary.workbook_id ? summary : item)),
      )
    }
  }

  function cacheDetail(detail: WorkbookDetailOut): void {
    details.value.set(workbookKey(detail.project_id, detail.workbook_id), detail)
    cacheSummary(detail)
  }

  function preserveAcceptedClaim<T extends WorkbookSummaryOut>(item: T, readRevision: number): T {
    const currentProgress = [
      detailFor(item.project_id, item.workbook_id)?.customer_progress,
      listFor(item.project_id).find((row) => row.workbook_id === item.workbook_id)
        ?.customer_progress,
    ].reduce(
      (newest, progress) =>
        progress && (progress.version ?? 0) > (newest?.version ?? -1) ? progress : newest,
      item.customer_progress,
    )
    item = { ...item, ...(currentProgress ? { customer_progress: currentProgress } : {}) }
    const accepted = acceptedCustomerClaims.get(workbookKey(item.project_id, item.workbook_id))
    const claim =
      accepted &&
      accepted.revision > readRevision &&
      accepted.claim.version > item.customer_claim.version
        ? accepted.claim
        : item.customer_claim
    return {
      ...item,
      customer_claim: newestCustomerClaim(item.project_id, item.workbook_id, claim),
    }
  }

  function newestCustomerClaim(
    projectId: number,
    workbookId: string,
    incoming: WorkbookSummaryOut['customer_claim'],
  ): WorkbookSummaryOut['customer_claim'] {
    return [
      listFor(projectId).find((item) => item.workbook_id === workbookId)?.customer_claim,
      detailFor(projectId, workbookId)?.customer_claim,
    ].reduce<WorkbookSummaryOut['customer_claim']>(
      (current, cached) =>
        cached &&
        (cached.version > current.version ||
          (cached.version === current.version && cached.claimant === null))
          ? cached
          : current,
      incoming,
    )
  }

  async function fetchList(projectId: number, query: NativeWorkbookQuery = {}): Promise<boolean> {
    const startedEpoch = epoch
    const readRevision = customerClaimRevision
    const key = projectKey(projectId)
    preparedQueryByProject.delete(key)
    const generation = (listGenerations.get(key) ?? 0) + 1
    requestedQueryByProject.set(key, { ...query })
    listGenerations.set(key, generation)
    loadingByProject.value.set(key, true)
    errorByProject.value.set(key, null)
    try {
      const response = await listNativeWorkbooks(projectId, query)
      if (!isCurrentListRequest(startedEpoch, key, generation)) {
        return false
      }
      const items = response.items.map((item) => preserveAcceptedClaim(item, readRevision))
      listByProject.value.set(key, items)
      totalByProject.value.set(key, response.total)
      queryByProject.value.set(key, { ...query })
      for (const item of items) cacheSummary(item)
      return true
    } catch (error) {
      if (!isCurrentListRequest(startedEpoch, key, generation)) {
        return false
      }
      const normalized = classifyApiError(error)
      errorByProject.value.set(key, normalized.message)
      throw error
    } finally {
      if (isCurrentListRequest(startedEpoch, key, generation)) {
        loadingByProject.value.set(key, false)
      }
    }
  }

  async function resolvePathAlias(projectId: number, path: string): Promise<WorkbookPathAliasOut> {
    const startedEpoch = epoch
    const alias = await resolveNativeWorkbookPath(projectId, path)
    requireCurrentEpoch(startedEpoch)
    return alias
  }

  async function fetchDetail(
    projectId: number,
    workbookId: string,
    options: { includeReview?: boolean } = {},
  ): Promise<NativeWorkbookSession> {
    const startedEpoch = epoch
    const readRevision = customerClaimRevision
    const key = workbookKey(projectId, workbookId)
    const generation = (detailGenerations.get(key) ?? 0) + 1
    detailGenerations.set(key, generation)
    const detailResponse = await fetchNativeWorkbook(projectId, workbookId)
    requireCurrentEpoch(startedEpoch)
    const review = options.includeReview
      ? await fetchNativeWorkbookReview(projectId, workbookId, identityFor(detailResponse))
      : null
    if (epoch !== startedEpoch || detailGenerations.get(key) !== generation) {
      throw new Error('The workbook request was superseded by newer navigation.')
    }
    const detail = preserveAcceptedClaim(detailResponse, readRevision)
    cacheDetail(detail)
    if (review) reviews.value.set(key, review)
    return { kind: 'native', projectId, workbookId, detail, review }
  }

  async function fetchReview(projectId: number, workbookId: string): Promise<WorkbookReviewOut> {
    const startedEpoch = epoch
    const key = workbookKey(projectId, workbookId)
    const detail = detailFor(projectId, workbookId)
    if (!detail) throw new Error('Reload the workbook before loading its review state.')
    const review = await fetchNativeWorkbookReview(projectId, workbookId, identityFor(detail))
    requireCurrentEpoch(startedEpoch)
    reviews.value.set(key, review)
    return review
  }

  async function fetchReviewers(projectId: number, force = false): Promise<WorkbookReviewerOut[]> {
    const key = projectKey(projectId)
    if (!force && reviewersByProject.value.has(key)) return reviewersFor(projectId)
    const startedEpoch = epoch
    const reviewers = await listNativeWorkbookReviewers(projectId)
    requireCurrentEpoch(startedEpoch)
    reviewersByProject.value.set(key, reviewers)
    return reviewers
  }

  async function fetchCustomerReviewers(
    projectId: number,
    force = false,
  ): Promise<WorkbookReviewerOut[]> {
    const key = projectKey(projectId)
    if (!force && customerReviewersByProject.value.has(key)) return customerReviewersFor(projectId)
    const startedEpoch = epoch
    const generation = (customerReviewerGenerations.get(key) ?? 0) + 1
    customerReviewerGenerations.set(key, generation)
    const reviewers = await listNativeWorkbookCustomerReviewers(projectId)
    requireCurrentEpoch(startedEpoch)
    if (customerReviewerGenerations.get(key) !== generation) {
      throw new Error('The reviewer list was superseded by a newer refresh.')
    }
    customerReviewersByProject.value.set(key, reviewers)
    return reviewers
  }

  function applyAssignment(
    projectId: number,
    workbookId: string,
    assignment: WorkbookAssignmentOut,
  ): void {
    const key = workbookKey(projectId, workbookId)
    const detail = details.value.get(key)
    if (detail) cacheDetail({ ...detail, assignment })
    const list = listByProject.value.get(projectKey(projectId))
    if (list) {
      listByProject.value.set(
        projectKey(projectId),
        list.map((item) => (item.workbook_id === workbookId ? { ...item, assignment } : item)),
      )
    }
  }

  async function recoverAssignment(projectId: number, workbookId: string): Promise<boolean> {
    try {
      await fetchDetail(projectId, workbookId, {
        includeReview: reviews.value.has(workbookKey(projectId, workbookId)),
      })
      return true
    } catch {
      return false
    }
  }

  async function refreshAssignedQueue(
    projectId: number,
    workbookId: string,
    startedEpoch: number,
  ): Promise<boolean> {
    const query = requestedQueryByProject.get(projectKey(projectId)) ?? queryFor(projectId)
    if (
      (!query.assignee || query.assignee === 'all') &&
      !query.assigneeIds?.length &&
      !query.includeUnassigned
    )
      return true
    try {
      if (!(await fetchList(projectId, query))) return true
      requireCurrentEpoch(startedEpoch)
      const lastPage = Math.max(1, Math.ceil(totalFor(projectId) / (query.pageSize ?? 20)))
      if ((query.page ?? 1) > lastPage) await fetchList(projectId, { ...query, page: lastPage })
      requireCurrentEpoch(startedEpoch)
      return true
    } catch {
      requireCurrentEpoch(startedEpoch)
      assignmentRecovery.value.set(workbookKey(projectId, workbookId), {
        message:
          'The current assignment is available, but the filtered list could not refresh. Retry refresh.',
        controlsDisabled: false,
      })
      return false
    }
  }

  function customerClaimPendingFor(projectId: number, workbookId: string): boolean {
    return customerClaimPending.value.has(workbookKey(projectId, workbookId))
  }

  function customerClaimRecoveryFor(
    projectId: number,
    workbookId: string,
  ): WorkbookAssignmentRecoveryState | null {
    return customerClaimRecovery.value.get(workbookKey(projectId, workbookId)) ?? null
  }

  async function refreshCustomerClaim(projectId: number, workbookId: string): Promise<boolean> {
    const startedEpoch = epoch
    const key = workbookKey(projectId, workbookId)
    if (customerClaimPending.value.has(key)) return false
    customerClaimPending.value.add(key)
    try {
      const accepted = await fetchList(
        projectId,
        requestedQueryByProject.get(projectKey(projectId)) ?? queryFor(projectId),
      )
      requireCurrentEpoch(startedEpoch)
      if (!accepted) return false
      customerClaimRecovery.value.delete(key)
      return true
    } finally {
      if (epoch === startedEpoch) customerClaimPending.value.delete(key)
    }
  }

  async function refreshFilteredCustomerQueue(
    projectId: number,
    workbookId: string,
    startedEpoch: number,
  ): Promise<WorkbookAssignmentMutationResult> {
    const query = requestedQueryByProject.get(projectKey(projectId)) ?? queryFor(projectId)
    if (query.review !== 'customer_visible' || !query.assignee || query.assignee === 'all')
      return { status: 'saved' }
    try {
      await fetchList(projectId, query)
      requireCurrentEpoch(startedEpoch)
      return { status: 'saved' }
    } catch {
      requireCurrentEpoch(startedEpoch)
      const state = {
        message: 'Assignment saved, but the workbook list could not be refreshed. Retry refresh.',
        controlsDisabled: true,
      }
      customerClaimRecovery.value.set(workbookKey(projectId, workbookId), state)
      return { status: 'saved', message: state.message }
    }
  }

  async function updateCustomerClaim(
    projectId: number,
    workbookId: string,
    assigneeUserId: string | null,
    expectedVersion: number,
  ): Promise<WorkbookAssignmentMutationResult> {
    const startedEpoch = epoch
    const key = workbookKey(projectId, workbookId)
    if (customerClaimPending.value.has(key))
      throw new Error('This assignment update is already in progress.')
    customerClaimPending.value.add(key)
    customerClaimRecovery.value.delete(key)
    try {
      const responseClaim = await updateNativeWorkbookCustomerClaim(
        projectId,
        workbookId,
        assigneeUserId,
        expectedVersion,
      )
      requireCurrentEpoch(startedEpoch)
      const detail = details.value.get(key)
      const list = listFor(projectId)
      // A peer update or access-expiry read may arrive before this PUT response.
      const customerClaim = newestCustomerClaim(projectId, workbookId, responseClaim)
      // Preserve accepted ownership in older reads without discarding navigation results.
      acceptedCustomerClaims.set(key, {
        revision: ++customerClaimRevision,
        claim: customerClaim,
      })
      if (detail) details.value.set(key, { ...detail, customer_claim: customerClaim })
      listByProject.value.set(
        projectKey(projectId),
        list.map((item) =>
          item.workbook_id === workbookId ? { ...item, customer_claim: customerClaim } : item,
        ),
      )
      return await refreshFilteredCustomerQueue(projectId, workbookId, startedEpoch)
    } catch (error) {
      requireCurrentEpoch(startedEpoch)
      const normalized = classifyApiError(error)
      if (normalized.code !== 'workbook_customer_claim_conflict' || normalized.status !== 409)
        throw error
      try {
        const accepted = await fetchList(
          projectId,
          requestedQueryByProject.get(projectKey(projectId)) ?? queryFor(projectId),
        )
        requireCurrentEpoch(startedEpoch)
        if (!accepted) throw new Error('The ownership refresh was superseded.', { cause: error })
        return { status: 'conflict_refreshed', message: normalized.message }
      } catch {
        requireCurrentEpoch(startedEpoch)
        const state = {
          message:
            'This assignment changed, but the current assignee could not be refreshed. Retry refresh.',
          controlsDisabled: true,
        }
        customerClaimRecovery.value.set(key, state)
        return { status: 'ownership_unknown', message: state.message }
      }
    } finally {
      if (epoch === startedEpoch) customerClaimPending.value.delete(key)
    }
  }

  async function updateAssignment(
    projectId: number,
    workbookId: string,
    assigneeUserId: string | null,
    expectedVersion: number,
  ): Promise<WorkbookAssignmentMutationResult> {
    const startedEpoch = epoch
    const key = workbookKey(projectId, workbookId)
    if (assignmentPending.value.has(key)) {
      throw new Error('This assignment update is already in progress.')
    }
    assignmentPending.value.add(key)
    assignmentRecovery.value.delete(key)
    try {
      const assignment = await updateNativeWorkbookAssignment(
        projectId,
        workbookId,
        assigneeUserId,
        expectedVersion,
      )
      requireCurrentEpoch(startedEpoch)
      applyAssignment(projectId, workbookId, assignment)
      if (!(await refreshAssignedQueue(projectId, workbookId, startedEpoch))) {
        return {
          status: 'saved_refresh_failed',
          message: assignmentRecoveryFor(projectId, workbookId)?.message,
        }
      }
      return { status: 'saved' }
    } catch (error) {
      requireCurrentEpoch(startedEpoch)
      const normalized = classifyApiError(error)
      if (normalized.code === 'workbook_assignee_ineligible') {
        await fetchReviewers(projectId, true).catch(() => undefined)
      }
      if (normalized.code !== 'workbook_assignment_conflict' || normalized.status !== 409) {
        throw error
      }
      const recovered = await recoverAssignment(projectId, workbookId)
      requireCurrentEpoch(startedEpoch)
      if (recovered) {
        if (!(await refreshAssignedQueue(projectId, workbookId, startedEpoch))) {
          return {
            status: 'conflict_refreshed',
            message: assignmentRecoveryFor(projectId, workbookId)?.message,
          }
        }
        const state = { message: normalized.message, controlsDisabled: false }
        assignmentRecovery.value.set(key, state)
        return { status: 'conflict_refreshed', message: state.message }
      }
      const state = {
        message: 'This assignment changed, but current ownership is unknown. Retry refresh.',
        controlsDisabled: true,
      }
      assignmentRecovery.value.set(key, state)
      return { status: 'ownership_unknown', message: state.message }
    } finally {
      if (epoch === startedEpoch) assignmentPending.value.delete(key)
    }
  }

  async function retryAssignmentRefresh(projectId: number, workbookId: string): Promise<boolean> {
    const startedEpoch = epoch
    const key = workbookKey(projectId, workbookId)
    if (assignmentPending.value.has(key)) return false
    assignmentPending.value.add(key)
    try {
      const refreshed = await recoverAssignment(projectId, workbookId)
      requireCurrentEpoch(startedEpoch)
      if (!refreshed || !(await refreshAssignedQueue(projectId, workbookId, startedEpoch)))
        return false
      assignmentRecovery.value.delete(key)
      return true
    } finally {
      if (epoch === startedEpoch) assignmentPending.value.delete(key)
    }
  }

  function removeCachedWorkbook(projectId: number, workbookId: string): void {
    const project = projectKey(projectId)
    const key = workbookKey(projectId, workbookId)
    const list = listByProject.value.get(project)
    details.value.delete(key)
    reviews.value.delete(key)
    if (!list?.some((item) => item.workbook_id === workbookId)) return
    listByProject.value.set(
      project,
      list.filter((item) => item.workbook_id !== workbookId),
    )
    totalByProject.value.set(project, Math.max(0, (totalByProject.value.get(project) ?? 1) - 1))
  }

  function lifecycleRefreshQuery(projectId: number, clampEmptyPage: boolean): NativeWorkbookQuery {
    const key = projectKey(projectId)
    const query = { ...(queryByProject.value.get(key) ?? {}) }
    if (clampEmptyPage && (query.page ?? 1) > 1 && listFor(projectId).length === 0) {
      query.page = Math.max(1, (query.page ?? 1) - 1)
      queryByProject.value.set(key, query)
    }
    return query
  }

  async function refreshLifecycleList(projectId: number, clampEmptyPage = false): Promise<void> {
    await fetchList(projectId, lifecycleRefreshQuery(projectId, clampEmptyPage))
  }

  async function refreshLifecycleDetail(
    projectId: number,
    workbookId: string,
    startedEpoch: number,
  ): Promise<'active' | 'removed'> {
    const readRevision = customerClaimRevision
    let detail: WorkbookDetailOut
    try {
      detail = await fetchNativeWorkbook(projectId, workbookId)
    } catch (error) {
      requireCurrentEpoch(startedEpoch)
      if (classifyApiError(error).status !== 404) throw error
      removeCachedWorkbook(projectId, workbookId)
      await refreshLifecycleList(projectId, true)
      requireCurrentEpoch(startedEpoch)
      return 'removed'
    }
    requireCurrentEpoch(startedEpoch)
    if (detail.availability_state === 'archived') {
      removeCachedWorkbook(projectId, workbookId)
      await refreshLifecycleList(projectId, true)
      requireCurrentEpoch(startedEpoch)
      return 'removed'
    }
    cacheDetail(preserveAcceptedClaim(detail, readRevision))
    await refreshLifecycleList(projectId)
    requireCurrentEpoch(startedEpoch)
    return 'active'
  }

  async function runLifecycleMutation(
    projectId: number,
    workbookId: string,
    mutate: () => Promise<WorkbookDetailOut>,
    mutationKind: 'rename' | 'archive',
  ): Promise<WorkbookLifecycleMutationResult> {
    const startedEpoch = epoch
    const readRevision = customerClaimRevision
    const key = workbookKey(projectId, workbookId)
    if (lifecyclePending.value.has(key)) {
      throw new Error('This workbook action is already in progress.')
    }
    lifecyclePending.value.add(key)
    lifecycleRecovery.value.delete(key)
    try {
      let result: WorkbookDetailOut
      try {
        result = await mutate()
      } catch (error) {
        requireCurrentEpoch(startedEpoch)
        const normalized = classifyApiError(error)
        if (normalized.status !== 409) throw error
        try {
          const state = await refreshLifecycleDetail(projectId, workbookId, startedEpoch)
          requireCurrentEpoch(startedEpoch)
          if (state === 'removed') return { status: 'removed', message: normalized.message }
          lifecycleRecovery.value.set(key, {
            message: normalized.message,
            controlsDisabled: false,
          })
          return { status: 'conflict_refreshed', message: normalized.message }
        } catch {
          requireCurrentEpoch(startedEpoch)
          const message =
            'The workbook changed, but its current state could not be refreshed. Retry refresh.'
          lifecycleRecovery.value.set(key, { message, controlsDisabled: true })
          return { status: 'state_unknown', message }
        }
      }
      requireCurrentEpoch(startedEpoch)
      if (mutationKind === 'rename') cacheDetail(preserveAcceptedClaim(result, readRevision))
      else removeCachedWorkbook(projectId, workbookId)
      await refreshLifecycleList(projectId, mutationKind === 'archive')
      requireCurrentEpoch(startedEpoch)
      return { status: mutationKind === 'rename' ? 'saved' : 'removed' }
    } finally {
      if (epoch === startedEpoch) lifecyclePending.value.delete(key)
    }
  }

  function renameWorkbook(
    projectId: number,
    workbookId: string,
    displayName: string,
    expectedVersion: number,
  ): Promise<WorkbookLifecycleMutationResult> {
    return runLifecycleMutation(
      projectId,
      workbookId,
      () => renameNativeWorkbook(projectId, workbookId, displayName, expectedVersion),
      'rename',
    )
  }

  function archiveWorkbook(
    projectId: number,
    workbookId: string,
    expectedVersion: number,
  ): Promise<WorkbookLifecycleMutationResult> {
    return runLifecycleMutation(
      projectId,
      workbookId,
      () => archiveNativeWorkbook(projectId, workbookId, expectedVersion),
      'archive',
    )
  }

  async function retryLifecycleRefresh(projectId: number, workbookId: string): Promise<boolean> {
    const startedEpoch = epoch
    const key = workbookKey(projectId, workbookId)
    if (lifecyclePending.value.has(key)) return false
    lifecyclePending.value.add(key)
    try {
      await refreshLifecycleDetail(projectId, workbookId, startedEpoch)
      requireCurrentEpoch(startedEpoch)
      lifecycleRecovery.value.delete(key)
      return true
    } catch {
      return false
    } finally {
      if (epoch === startedEpoch) lifecyclePending.value.delete(key)
    }
  }

  async function refreshForRead(projectId: number, workbookId: string): Promise<WorkbookDetailOut> {
    const includeReview = reviews.value.has(workbookKey(projectId, workbookId))
    return (await fetchDetail(projectId, workbookId, { includeReview })).detail
  }

  async function loadContent(
    projectId: number,
    workbookId: string,
    version: 'original' | 'effective' = 'effective',
    signal?: AbortSignal,
    expectedSnapshotToken?: string,
  ): Promise<Blob> {
    const startedEpoch = epoch
    const detail = detailFor(projectId, workbookId)
    const listed = listFor(projectId).find((item) => item.workbook_id === workbookId)
    const snapshotToken = expectedSnapshotToken ?? detail?.snapshot_token ?? listed?.snapshot_token
    if (!snapshotToken) throw new Error('Reload the workbook before loading its content.')
    try {
      const blob = await fetchNativeWorkbookBlob(
        projectId,
        workbookId,
        snapshotToken,
        version,
        signal,
      )
      requireCurrentEpoch(startedEpoch)
      return blob
    } catch (error) {
      requireCurrentEpoch(startedEpoch)
      if (
        expectedSnapshotToken !== undefined ||
        !REFRESHABLE_READ_CODES.has(classifyApiError(error).code ?? '') ||
        signal?.aborted
      ) {
        throw error
      }
      const refreshed = await refreshForRead(projectId, workbookId)
      const blob = await fetchNativeWorkbookBlob(
        projectId,
        workbookId,
        refreshed.snapshot_token,
        version,
        signal,
      )
      requireCurrentEpoch(startedEpoch)
      return blob
    }
  }

  async function loadSourceCard(
    projectId: number,
    workbookId: string,
    identity: NativeWorkbookIdentity,
    rowId: string,
    signal?: AbortSignal,
  ): Promise<Blob> {
    const startedEpoch = epoch
    try {
      const blob = await fetchNativeSourceCardBlob(
        projectId,
        workbookId,
        identity.generationId,
        rowId,
        identity.effectiveHash,
        signal,
      )
      requireCurrentEpoch(startedEpoch)
      return blob
    } catch (error) {
      requireCurrentEpoch(startedEpoch)
      if (!REFRESHABLE_READ_CODES.has(classifyApiError(error).code ?? '') || signal?.aborted) {
        throw error
      }
      const refreshed = await refreshForRead(projectId, workbookId)
      if (!refreshed.effective_rows?.some((row) => row.row_id === rowId)) throw error
      const blob = await fetchNativeSourceCardBlob(
        projectId,
        workbookId,
        refreshed.current_generation_id,
        rowId,
        refreshed.effective_content_hash,
        signal,
      )
      requireCurrentEpoch(startedEpoch)
      return blob
    }
  }

  async function loadInputSourceCard(
    projectId: number,
    workbookId: string,
    identity: NativeWorkbookIdentity,
    sourceFileVersionId: string,
    signal?: AbortSignal,
  ): Promise<Blob> {
    const startedEpoch = epoch
    try {
      const blob = await fetchNativeInputSourceCardBlob(
        projectId,
        workbookId,
        identity.generationId,
        sourceFileVersionId,
        identity.effectiveHash,
        signal,
      )
      requireCurrentEpoch(startedEpoch)
      return blob
    } catch (error) {
      requireCurrentEpoch(startedEpoch)
      if (!REFRESHABLE_READ_CODES.has(classifyApiError(error).code ?? '') || signal?.aborted) {
        throw error
      }
      const refreshed = await refreshForRead(projectId, workbookId)
      if (
        refreshed.current_generation_id !== identity.generationId ||
        !refreshed.source_cards?.some((card) => card.source_file_version_id === sourceFileVersionId)
      )
        throw error
      const blob = await fetchNativeInputSourceCardBlob(
        projectId,
        workbookId,
        refreshed.current_generation_id,
        sourceFileVersionId,
        refreshed.effective_content_hash,
        signal,
      )
      requireCurrentEpoch(startedEpoch)
      return blob
    }
  }

  async function saveCorrections(
    projectId: number,
    workbookId: string,
    identity: NativeWorkbookIdentity,
    edits: CellEdit[],
    idempotencyId: string,
    rowInsertions: WorkbookRowInsertion[] = [],
    rowDeletions: WorkbookRowDeletion[] = [],
  ): Promise<WorkbookDetailOut> {
    const startedEpoch = epoch
    const readRevision = customerClaimRevision
    const result = await saveNativeWorkbookCorrections(
      projectId,
      workbookId,
      identity,
      edits,
      idempotencyId,
      rowInsertions,
      rowDeletions,
    )
    requireCurrentEpoch(startedEpoch)
    const detail = preserveAcceptedClaim(result, readRevision)
    cacheDetail(detail)
    return detail
  }

  async function renderCorrections(
    projectId: number,
    workbookId: string,
    identity: NativeWorkbookIdentity,
    edits: CellEdit[],
    rowInsertions: WorkbookRowInsertion[] = [],
    rowDeletions: WorkbookRowDeletion[] = [],
  ): Promise<Blob> {
    const startedEpoch = epoch
    const blob = await renderNativeWorkbookCorrections(
      projectId,
      workbookId,
      identity,
      edits,
      rowInsertions,
      rowDeletions,
    )
    requireCurrentEpoch(startedEpoch)
    return blob
  }

  async function resetCorrections(
    projectId: number,
    workbookId: string,
    identity: NativeWorkbookIdentity,
  ): Promise<WorkbookDetailOut> {
    const startedEpoch = epoch
    const readRevision = customerClaimRevision
    const result = await resetNativeWorkbookCorrections(projectId, workbookId, identity)
    requireCurrentEpoch(startedEpoch)
    const detail = preserveAcceptedClaim(result, readRevision)
    cacheDetail(detail)
    return detail
  }

  async function setReviewStatus(
    projectId: number,
    workbookId: string,
    identity: NativeWorkbookIdentity,
    expectedRevision: number,
    status: 'needs_review' | 'ready_for_customer',
  ): Promise<WorkbookReviewOut> {
    const startedEpoch = epoch
    const result = await setNativeWorkbookReviewStatus(
      projectId,
      workbookId,
      identity,
      expectedRevision,
      status,
    )
    requireCurrentEpoch(startedEpoch)
    reviews.value.set(workbookKey(projectId, workbookId), result)
    return result
  }

  async function verifyRows(
    projectId: number,
    workbookId: string,
    identity: NativeWorkbookIdentity,
    rowIndices: number[],
  ): Promise<WorkbookReviewOut> {
    const startedEpoch = epoch
    const result = await verifyNativeWorkbookRows(projectId, workbookId, identity, rowIndices)
    requireCurrentEpoch(startedEpoch)
    reviews.value.set(workbookKey(projectId, workbookId), result)
    return result
  }

  async function notificationContext(
    projectId: number,
    workbookId: string,
  ): Promise<WorkbookNotificationContextOut> {
    const startedEpoch = epoch
    const context = await fetchNativeNotificationContext(projectId, workbookId)
    requireCurrentEpoch(startedEpoch)
    return context
  }

  async function notify(
    projectId: number,
    workbookId: string,
    request: NativeWorkbookNotificationRequest,
  ): Promise<CustomerWorkbookNotificationOut> {
    const startedEpoch = epoch
    const result = await sendNativeCustomerNotifications(
      projectId,
      workbookId,
      request.expectedEffectiveHash,
      request,
    )
    requireCurrentEpoch(startedEpoch)
    return result
  }

  return {
    workbookForScope,
    fetchScopeWorkbook,
    portalStatisticsFor,
    fetchPortalStatistics,
    customerProgressPendingFor,
    updateCustomerProgress,
    listFor,
    totalFor,
    queryFor,
    loadingFor,
    markPreparedList,
    consumePreparedList,
    errorFor,
    detailFor,
    reviewFor,
    reviewersFor,
    customerReviewersFor,
    fetchCustomerReviewers,
    assignmentPendingFor,
    customerClaimPendingFor,
    customerClaimRecoveryFor,
    updateCustomerClaim,
    refreshCustomerClaim,
    assignmentRecoveryFor,
    lifecyclePendingFor,
    lifecycleRecoveryFor,
    reset,
    fetchList,
    resolvePathAlias,
    fetchDetail,
    fetchReview,
    fetchReviewers,
    updateAssignment,
    retryAssignmentRefresh,
    renameWorkbook,
    archiveWorkbook,
    retryLifecycleRefresh,
    loadContent,
    loadSourceCard,
    loadInputSourceCard,
    saveCorrections,
    renderCorrections,
    resetCorrections,
    setReviewStatus,
    verifyRows,
    notificationContext,
    notify,
  }
})
